import { memo, useId } from "react";
import { useReducedMotion } from "motion/react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ANIM_MS,
  AXIS_TICK,
  CHART_HEIGHT,
  CURSOR_FILL,
  CURSOR_LINE,
  formatCompact,
} from "./chartTheme";
import { CartesianLegend, CartesianTooltip } from "./ChartChrome";
import { isHorizontal, type NormalizedChart } from "./resolveChart";

const cartesianMargin = { top: 4, right: 10, left: 0, bottom: 0 } as const;

function axisLabel(value: string | undefined, axis: "x" | "y") {
  if (!value) return undefined;
  return axis === "x"
    ? { value, position: "insideBottom" as const, offset: -2, fontSize: 11, fill: "#94a3b8" }
    : { value, angle: -90, position: "insideLeft" as const, fontSize: 11, fill: "#94a3b8" };
}

export const CartesianChart = memo(function CartesianChart({ chart }: { chart: NormalizedChart }) {
  const reduce = useReducedMotion();
  const uid = useId().replace(/:/g, "");
  const horizontal = isHorizontal(chart.kind);
  const rowsN = chart.rows.length;

  const anim = {
    isAnimationActive: !reduce,
    animationDuration: ANIM_MS,
    animationEasing: "ease-out" as const,
  };

  const height = horizontal
    ? Math.min(360, Math.max(160, rowsN * 34 + 52))
    : CHART_HEIGHT.cartesian;

  const longestCat = chart.rows.reduce(
    (m, r) => Math.max(m, String(r[chart.categoryKey] ?? "").length),
    0,
  );
  const yAxisWidth = Math.min(172, Math.max(56, Math.round(longestCat * 6.4 + 16)));

  const isBars = chart.kind === "bar";
  const angled = isBars && (rowsN > 4 || longestCat > 6);
  const xCatProps = angled
    ? { angle: -30, textAnchor: "end" as const, height: 58, interval: 0, tickMargin: 8 }
    : { height: chart.xLabel ? 40 : 24, tickMargin: 8, interval: (isBars ? 0 : "preserveStartEnd") as 0 | "preserveStartEnd" };

  const numericTick = { tick: AXIS_TICK, tickLine: false, axisLine: false } as const;
  const cartesianTooltip = (
    <CartesianTooltip unit={chart.unit} singleSeries={chart.singleSeries} total={chart.total} />
  );

  let body: React.ReactElement;

  if (chart.kind === "bar" || horizontal) {
    body = (
      <BarChart data={chart.rows} layout={horizontal ? "vertical" : "horizontal"} margin={cartesianMargin}>
        <CartesianGrid strokeDasharray="3 3" horizontal={!horizontal} vertical={horizontal} />
        {horizontal ? (
          <>
            <XAxis type="number" {...numericTick} tickFormatter={formatCompact} label={axisLabel(chart.xLabel, "x")} />
            <YAxis type="category" dataKey={chart.categoryKey} {...numericTick} width={yAxisWidth} />
          </>
        ) : (
          <>
            <XAxis dataKey={chart.categoryKey} tick={AXIS_TICK} tickLine={false} axisLine={false} label={axisLabel(chart.xLabel, "x")} {...xCatProps} />
            <YAxis type="number" {...numericTick} width={40} tickFormatter={formatCompact} label={axisLabel(chart.yLabel, "y")} />
          </>
        )}
        <Tooltip cursor={{ fill: CURSOR_FILL }} content={cartesianTooltip} />
        {chart.singleSeries ? (
          <Bar
            dataKey={chart.series[0].key}
            radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
            maxBarSize={horizontal ? 26 : 40}
            {...anim}
          >
            {chart.rows.map((r, i) => (
              <Cell key={i} fill={r.fill} />
            ))}
          </Bar>
        ) : (
          chart.series.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              fill={s.color}
              stackId={chart.stacked ? "stack" : undefined}
              radius={horizontal ? [0, 3, 3, 0] : [3, 3, 0, 0]}
              maxBarSize={horizontal ? 22 : 32}
              {...anim}
            />
          ))
        )}
      </BarChart>
    );
  } else if (chart.kind === "area") {
    body = (
      <AreaChart data={chart.rows} margin={cartesianMargin}>
        <defs>
          {chart.series.map((s) => (
            <linearGradient key={s.key} id={`${uid}-a-${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity={0.28} />
              <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey={chart.categoryKey} tick={AXIS_TICK} tickLine={false} axisLine={false} label={axisLabel(chart.xLabel, "x")} {...xCatProps} />
        <YAxis type="number" {...numericTick} width={40} tickFormatter={formatCompact} label={axisLabel(chart.yLabel, "y")} />
        <Tooltip cursor={{ stroke: CURSOR_LINE, strokeWidth: 1, strokeDasharray: "3 3" }} content={cartesianTooltip} />
        {chart.series.map((s) => (
          <Area
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={s.color}
            strokeWidth={2.5}
            fill={`url(#${uid}-a-${s.key})`}
            stackId={chart.stacked ? "stack" : undefined}
            activeDot={{ r: 4, strokeWidth: 0 }}
            {...anim}
          />
        ))}
      </AreaChart>
    );
  } else {
    body = (
      <LineChart data={chart.rows} margin={cartesianMargin}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey={chart.categoryKey} tick={AXIS_TICK} tickLine={false} axisLine={false} label={axisLabel(chart.xLabel, "x")} {...xCatProps} />
        <YAxis type="number" {...numericTick} width={40} tickFormatter={formatCompact} label={axisLabel(chart.yLabel, "y")} />
        <Tooltip cursor={{ stroke: CURSOR_LINE, strokeWidth: 1, strokeDasharray: "3 3" }} content={cartesianTooltip} />
        {chart.series.map((s) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={s.color}
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
            {...anim}
          />
        ))}
      </LineChart>
    );
  }

  const legend =
    !chart.singleSeries && (chart.legend === "top" || chart.legend === "bottom") ? (
      <CartesianLegend series={chart.series} />
    ) : null;

  return (
    <div>
      {chart.legend === "top" && legend}
      <ResponsiveContainer width="100%" height={height}>
        {body}
      </ResponsiveContainer>
      {chart.legend === "bottom" && legend}
    </div>
  );
});
